import { inject, injectable } from "inversify";
import Connect from "../connect/Connect";
import InitResult from "../connect/result/InitResult";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import { mobileAndTabletCheck, tabletCheck } from "../device/device";
import IErrorHandler from "../error/IErrorHandler";
import { ERROR_CODES } from "../error/errorCodes";
import events from "../events/EventsObservable";
import { EVENTS } from "../events/baseEvents";
import Replay from "../replay/Replay";
import Settings from "../settings/Settings";
import AbstractUI from "../ui/AbstractUI";
import InitWallet from "../wallet/InitWallet";
import SystemConfig, { CONFIG_PARAMS, MODES } from "./SystemConfig";

@injectable()
export default class SystemInit {
	private config: SystemConfig;
	private connect: Connect;
	private ui: AbstractUI;
	private initWallet: InitWallet;
	private errorHandler: IErrorHandler;
	private replay: Replay;

	constructor(
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.Connect) connect: Connect,
		@inject(SERVICE_IDENTIFIER.ErrorHandler) errorHandler: IErrorHandler,
		@inject(SERVICE_IDENTIFIER.UI) ui: AbstractUI,
		@inject(SERVICE_IDENTIFIER.InitWallet) initWallet: InitWallet,
		@inject(SERVICE_IDENTIFIER.Replay) replay: Replay
	) {
		this.config = config;
		this.connect = connect;
		this.errorHandler = errorHandler;
		this.ui = ui;
		this.initWallet = initWallet;
		this.replay = replay;
	}

	async init(settings) {
		let params = this.getParams();

		await events.dispatch(EVENTS.PREINIT_SUCCESS, settings);

		if (this.config.get(CONFIG_PARAMS.MODE) === MODES.REPLAY) {
			await this.replay.init(params, settings);
		} else {
			await this.session(params, settings);
		}
	}

	async session(params: any = this.getParams(), settings: Settings) {
		let conn;
		await this.getOpenSessions(params, settings);

		try {
			conn = await this.connect.init(params);
		} catch (e) {
			console.log(e);
			await this.handleOpenSessions(e);
			await this.errorHandler.handleError(e);
			return Promise.reject(e);
		}

		let initResult = conn;

		let platformSettings = this.isPlaytech()
			? await this.getPlatformSettings(initResult)
			: undefined;

		await this.onInitSuccess(initResult, settings, platformSettings);
		await this.initWebSocket(settings);
	}

	private async handleOpenSessions(e) {
		if (e?.code === ERROR_CODES.ANOTHER_GAME_OPEN) {
			if (e?.error?.response?.data?.player_id) {
				this.config.set(
					CONFIG_PARAMS.PLAYER_ID,
					e?.error?.response?.data?.player_id
				);
			}
			await this.closeOpenSessions(e?.error.response);
		}
	}

	private async getOpenSessions(params: any, settings: Settings) {
		if (
			this.config.get(CONFIG_PARAMS.MODE) === MODES.REAL &&
			params.playerId &&
			settings.getCloseSessions()
		) {
			let conn;
			try {
				// settings.close_sessions can be open_sessions, open_game_sessions or null
				if (settings.getCloseSessions() === "open_sessions") {
					conn = await this.connect.getSessions(params);
				}
				if (settings.getCloseSessions() === "open_game_sessions") {
					conn = await this.connect.getGameSessions(params);
				}
			} catch (e) {
				console.log(e);
				await this.errorHandler.handleError(e);
				return Promise.reject(e);
			}
			await this.closeOpenSessions(conn);
		}
	}

	private async closeOpenSessions(data) {
		if (data?.data?.sessions) {
			for (let session of data.data.sessions) {
				let params = {
					serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
					casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
					sessionId: session,
					soft: false,
				};
				try {
					await this.connect.close(params);
				} catch (e) {
					console.log(e);
				}
			}
		}
	}

	private async getPlatformSettings(initResult: InitResult) {
		let params = {
			serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
			sessionId: initResult.getSessionId(),
		};

		try {
			return await this.connect.platformSettings(params);
		} catch (e) {
			return undefined;
		}
	}

	/**
	 * @param {InitResult} initResult
	 * @param {Settings} settings
	 * @param {PlatformSettings} platformSettings
	 * @private
	 */
	private async onInitSuccess(initResult, settings, platformSettings) {
		this.config.initResult(initResult);
		this.errorHandler.configure(settings.getSkipIdOnError());
		await this.initWallet.init(initResult, settings, platformSettings);
		this.ui.init();
		await events.dispatch(EVENTS.INIT_SUCCESS, initResult);
	}

	private async initWebSocket(settings) {
		if (!settings.getWebSocket()) {
			return Promise.resolve();
		}

		this.config.set(CONFIG_PARAMS.WEB_SOCKET, settings.getWebSocket());

		let params = {
			sessionId: this.config.get(CONFIG_PARAMS.SESSION_ID),
			sessionKey: this.config.get(CONFIG_PARAMS.SESSION_KEY),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
		};

		return this.connect.socket(params);
	}

	getParams() {
		let params = {
			serverApi: this.config.get(CONFIG_PARAMS.SERVER_API),
			gameId: this.config.get(CONFIG_PARAMS.GAME_ID),
			playerId: this.config.get(CONFIG_PARAMS.PLAYER_ID),
			casinoId: this.config.get(CONFIG_PARAMS.CASINO_ID),
			currency: this.config.get(CONFIG_PARAMS.CURRENCY),
			mode: this.config.get(CONFIG_PARAMS.MODE),
			sessionKey: this.config.get(CONFIG_PARAMS.SESSION_KEY),
			device: this.getPlatform(),
			internal: this.config.get(CONFIG_PARAMS.INTERNAL),
			replayRound: this.config.get(CONFIG_PARAMS.REPLAY_ROUND),
			replaySession: this.config.get(CONFIG_PARAMS.REPLAY_SESSION)
		};

		if (this.config.get(CONFIG_PARAMS.MODE) === MODES.REPLAY) {
			params.mode = MODES.FREE;
		}

		return params;
	}

	/*
	 *   class Device(IntEnum):
	 *   """Enum of device where web games could run.
	 *   """
	 *   undefined = 0
	 *   desktop = 1
	 *   mobile = 2
	 *   tablet = 3
	 */
	private getPlatform() {
		if (this.config.get(CONFIG_PARAMS.EXHIBITION)) {
			return 0;
		} else if (!mobileAndTabletCheck()) {
			return 1;
		} else if (tabletCheck()) {
			return 3;
		} else {
			return 2;
		}
	}

	private isPlaytech(): boolean {
		return this.config.get(CONFIG_PARAMS.PLATFORM).includes("playtech");
	}
}
